import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, Select, Stack, Typography, TextField, Collapse } from '@mui/material';
import isEqualObj from 'fast-deep-equal/es6/react';
import {
  BillingCredentialData,
  ConfigResponse,
  JSONValue,
  MerchantExternalCredential,
  NonAcceptedCardBrandsValue,
  PaymentGatewayType,
  findPaymentGatewayType,
  normalizeGateway,
  SettlementTypeValue,
  SplitPaymentValue,
  PartialPaymentValue,
  SupportedCountriesValue,
  ShopperCredentialSharingValue,
  AdditionalTransactionDataValue,
  PaymentCallback,
  CallbackResponse,
  BankCredentialData,
  DEFAULT_BANK_SYSTEM_OF_ORIGIN,
} from '../../../store/modules/merchant/types';
import {
  defaultAdditionalTransactionDataValue,
  defaultPaymentCredentialSharingValue,
} from '../../../store/modules/merchant/constants';
import PaymentCapture from './PaymentCapture';
import PaymentGateway from './PaymentGateway';
import { ConfigSwitchSection, ParentAwareSaveBar } from '../../../common';
import { InheritedConfigSettingCallouts } from '../../../common/InheritedConfigSettingCallouts';
import { useChildInheritedConfigEdit } from '../../../hooks/useChildInheritedConfigEdit';
import {
  isCallbackInheritedForMerchant,
  isConfigInheritedForMerchant,
} from '../../../util/configInheritance';
import { useMerchant } from '../../../contexts/MerchantContext';
import { CrossBorderPayments } from './CrossBorderPayments';
import { PaymentCallbackErrors, PaymentCredentialSharing } from './PaymentCredentialSharing';
import { AdditionalTransactionData } from './AdditionalTransactionData';
import CardAcceptance from './CardAcceptance';
import BillingCredentials from './BillingCredentials';
import { isCredentialFilled } from './PaymentGatewayAccordions';
import { ConfigToUpsert } from '../../../hooks/useConfigUpsert';
import { merchantDomainErrorMessage as validateCallbackUrl } from '../../../util/helpers';
import { useIsSkipifyAdmin } from '../../../hooks';
import { CallbackToUpsert } from '../../../hooks/useCallbackUpsert';
import { Alert } from '../../Paylinks/components/Alert';
import {
  getPartialAuthToggleDisabledReason,
  getPartialPaymentToggleDisabledReason,
  getSplitPaymentsToggleDisabledReason,
  isPartialAuthorizationEnabled,
  isPartialPaymentEnabled,
} from '../../../util/partialPaymentConfig';
import { CardBrand } from '../../../util/constants';
import {
  CARD_ACCEPTANCE_MIN_ONE_HINT,
  hasAtLeastOneAcceptedCardNetwork,
} from '../../../util/cardAcceptance';
import {
  getCallbackDisplayLabel,
  getChildCustomizeIdleSaveBarDetailText,
  inheritableLabelsFromConfigNames,
  uniqueDisplayLabels,
} from '../../../util/parentSettingSave';

/**
 * Validates the header key and value pair
 * @param headerKey The header key
 * @param headerValue The header value
 * @returns An object containing validation errors or null values if valid
 */
const validateHeaders = (
  headerKey?: string,
  headerValue?: string,
): { keyError: string | null; valueError: string | null } => {
  const keyProvided = !!headerKey?.trim();
  const valueProvided = !!headerValue?.trim();

  // Only need to check two cases that cause errors
  if (keyProvided && !valueProvided) {
    return { keyError: null, valueError: 'Value is required when Key is provided' };
  }

  if (!keyProvided && valueProvided) {
    return { keyError: 'Key is required when Value is provided', valueError: null };
  }

  // All other cases (both provided or both empty) are valid
  return { keyError: null, valueError: null };
};

const validateCallbackSettings = (callback: PaymentCallback): PaymentCallbackErrors => {
  const errors: PaymentCallbackErrors = { url: null, headerKey: null, headerValue: null };
  if (!callback.url || callback.url.trim() === '') {
    errors.url = 'Callback URL is required when credential sharing is enabled';
    return errors;
  }

  const callbackError = validateCallbackUrl(callback.url ?? '');
  if (callbackError.length > 0) {
    errors.url = callbackError;
    return errors;
  }

  const { keyError, valueError } = validateHeaders(callback.headerKey, callback.headerValue);
  errors.headerKey = keyError;
  errors.headerValue = valueError;

  return errors;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fields = [
  'PARTIAL_AUTHORIZATIONS',
  'PARTIAL_PAYMENTS',
  'IS_PAYMENT_PLATFORM',
  'SPLIT_PAYMENTS',
  'SUPPORTED_COUNTRIES',
  'SETTLEMENT_TYPE',
  'AVS_DECLINE',
  'NON_ACCEPTED_CARD_BRANDS',
  'CREDENTIAL',
  'BILLING_CREDENTIAL',
  'PAYMENT_GATEWAY',
  'SHOPPER_CREDENTIAL_ACCESS',
  'PAYMENT_SHARING_CALLBACK',
  'ADDITIONAL_TRANSACTION_DATA',
  'BANK_ENABLED',
  'BANK_CREDENTIAL',
] as const;
type Field = (typeof fields)[number];

const INHERITABLE_DIRTY_FIELD_CONFIGS: Partial<Record<Field, ConfigResponse['name']>> = {
  SETTLEMENT_TYPE: 'SETTLEMENT_TYPE',
  AVS_DECLINE: 'AVS_DECLINE',
  NON_ACCEPTED_CARD_BRANDS: 'NON_ACCEPTED_CARD_BRANDS',
  PARTIAL_AUTHORIZATIONS: 'PARTIAL_AUTHORIZATIONS',
  PARTIAL_PAYMENTS: 'PARTIAL_PAYMENTS',
  IS_PAYMENT_PLATFORM: 'IS_PAYMENT_PLATFORM',
  SPLIT_PAYMENTS: 'SPLIT_PAYMENTS',
  SUPPORTED_COUNTRIES: 'SUPPORTED_COUNTRIES',
  SHOPPER_CREDENTIAL_ACCESS: 'SHOPPER_CREDENTIAL_ACCESS',
  ADDITIONAL_TRANSACTION_DATA: 'ADDITIONAL_TRANSACTION_DATA',
};

function areNonAcceptedCardBrandsEqual(
  local: CardBrand[] | undefined,
  baseline: CardBrand[] | undefined,
): boolean {
  const sortBrands = (brands: CardBrand[]) => [...brands].sort();
  return isEqualObj(sortBrands(local ?? []), sortBrands(baseline ?? []));
}

function isSplitPaymentsStateEqualToConfig(
  local: { enabled: boolean; maxPayments?: number },
  config?: ConfigResponse<SplitPaymentValue> | null,
): boolean {
  if (local.enabled !== !!config?.enabled) {
    return false;
  }
  if (!local.enabled) {
    return true;
  }
  return (local.maxPayments || 2) === (config?.value?.maxPayments ?? 2);
}

function getInheritableSaveBarLabelsForDirtyFields(dirtyFields: Set<Field>): string[] {
  const configNames = [...dirtyFields]
    .map((field) => INHERITABLE_DIRTY_FIELD_CONFIGS[field])
    .filter((name): name is ConfigResponse['name'] => Boolean(name));
  const labels = inheritableLabelsFromConfigNames(configNames);
  if (dirtyFields.has('PAYMENT_SHARING_CALLBACK')) {
    labels.push(getCallbackDisplayLabel('ShopperCredentialSharing'));
  }
  return uniqueDisplayLabels(labels);
}

const PAYMENTS_AND_PROCESSING_INHERITABLE_CONFIG_NAMES = [
  ...new Set(
    Object.values(INHERITABLE_DIRTY_FIELD_CONFIGS).filter((name): name is ConfigResponse['name'] =>
      Boolean(name),
    ),
  ),
];

export function isPaymentsProcessingSettingInherited(
  merchantId: string,
  configs: ConfigResponse[],
  callbacks: CallbackResponse[],
): boolean {
  const hasInheritedConfig = PAYMENTS_AND_PROCESSING_INHERITABLE_CONFIG_NAMES.some((name) => {
    const cfg = configs.find((config) => config.name === name);
    if (!cfg?.configId) {
      return false;
    }
    return isConfigInheritedForMerchant(merchantId, cfg);
  });
  const paymentSharingCallback = callbacks.find(
    (cb) => cb.callbackType === 'ShopperCredentialSharing',
  );
  return (
    hasInheritedConfig ||
    isCallbackInheritedForMerchant(merchantId, paymentSharingCallback, {
      missingIsInherited: false,
    })
  );
}

export type OnSaveChanges = {
  credentials?: Omit<MerchantExternalCredential, 'merchantId' | 'credentialId'>[];
  selectedGateway?: PaymentGatewayType;
  configs?: ConfigToUpsert[];
  callbacks?: CallbackToUpsert[];
  bankEnabled?: boolean;
};

type Props = {
  configs: ConfigResponse[];
  callbacks: CallbackResponse[];
  paymentCredentials: MerchantExternalCredential<PaymentGatewayType>[];
  bankCredential?: MerchantExternalCredential<'aeropay', BankCredentialData>;
  bankEnabled: boolean;
  selectedPaymentCredential?: PaymentGatewayType;
  isCustomMerchant: boolean;
  readOnly?: boolean;
  usingBillingFields?: boolean;
  billingCredential?: MerchantExternalCredential<'Worldpay_Billing', BillingCredentialData>;
  merchantName: string;
  onSave: (changes: OnSaveChanges) => Promise<void>;
  isSaving: boolean;
};

export const PaymentsAndProcessingForm = ({
  configs,
  callbacks,
  paymentCredentials,
  bankCredential,
  billingCredential,
  isCustomMerchant,
  readOnly = false,
  usingBillingFields = false,
  merchantName,
  selectedPaymentCredential,
  isSaving,
  onSave,
  bankEnabled,
}: Props) => {
  const { merchantId, hasDescendants } = useMerchant();
  const isSkipifyAdmin = useIsSkipifyAdmin();

  const settingInherited = useMemo(
    () => isPaymentsProcessingSettingInherited(merchantId, configs, callbacks),
    [merchantId, configs, callbacks],
  );
  const {
    showInheritedCallouts,
    isCustomizingInherited,
    controlsDisabled: inheritableSettingsDisabled,
    onCustomize,
    resetCustomizing,
    getSaveBarInheritanceProps,
    isChildInheritedSaveBarOpen,
  } = useChildInheritedConfigEdit(settingInherited, readOnly);

  useEffect(() => {
    resetCustomizing();
  }, [settingInherited, resetCustomizing]);

  const [dirtyFields, setDirtyFields] = useState<Set<Field>>(new Set());
  const handleFieldChange = useCallback((field: Field, isEqual: boolean) => {
    setDirtyFields((prev) => {
      const next = new Set(prev);
      if (isEqual) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  }, []);

  const getConfig = <T,>(name: string) =>
    configs.find((config) => config.name === name) as ConfigResponse<T> | undefined;

  const partialAuthConfig = getConfig<boolean>('PARTIAL_AUTHORIZATIONS');
  const partialPaymentConfig = getConfig<PartialPaymentValue>('PARTIAL_PAYMENTS');
  const isPaymentPlatformConfig = getConfig<boolean>('IS_PAYMENT_PLATFORM');
  const splitPaymentsConfig = getConfig<SplitPaymentValue>('SPLIT_PAYMENTS');
  const supportedCountriesConfig = getConfig<SupportedCountriesValue>('SUPPORTED_COUNTRIES');
  const settlementTypeConfig = getConfig<SettlementTypeValue>('SETTLEMENT_TYPE');
  const avsDeclineConfig = getConfig<boolean>('AVS_DECLINE');
  const nonAcceptedCardBrandsConfig = getConfig<NonAcceptedCardBrandsValue>(
    'NON_ACCEPTED_CARD_BRANDS',
  );
  const shopperCredentialSharingConfig = getConfig<ShopperCredentialSharingValue>(
    'SHOPPER_CREDENTIAL_ACCESS',
  );
  const additionalTransactionDataConfig = getConfig<AdditionalTransactionDataValue>(
    'ADDITIONAL_TRANSACTION_DATA',
  );

  const paymentSharingCallback = callbacks.find(
    (cb) => cb.callbackType === 'ShopperCredentialSharing',
  );

  const [localSupportedCountries, setLocalSupportedCountries] = useState({
    enabled: !!supportedCountriesConfig?.enabled,
    value: supportedCountriesConfig?.value,
  });
  const [localPartialAuthEnabled, setLocalPartialAuthEnabled] = useState(
    isPartialAuthorizationEnabled(partialAuthConfig),
  );
  const [localPartialPaymentEnabled, setLocalPartialPaymentEnabled] = useState(
    isPartialPaymentEnabled(partialPaymentConfig),
  );
  const [localPaymentPlatformEnabled, setLocalPaymentPlatformEnabled] = useState(
    !!isPaymentPlatformConfig?.value,
  );
  const [localSplitPaymentEnabled, setLocalSplitPaymentEnabled] = useState({
    enabled: !!splitPaymentsConfig?.enabled,
    maxPayments: splitPaymentsConfig?.value?.maxPayments,
  });

  const partialPaymentToggleDisabledReason = getPartialPaymentToggleDisabledReason(
    localPartialAuthEnabled,
    localSplitPaymentEnabled.enabled,
  );
  const partialAuthToggleDisabledReason = getPartialAuthToggleDisabledReason(
    localPartialPaymentEnabled,
  );
  const splitPaymentsToggleDisabledReason = getSplitPaymentsToggleDisabledReason(
    localPartialPaymentEnabled,
  );

  const [localSettlementType, setLocalSettlementType] = useState(settlementTypeConfig?.value);
  const [localAvsDecline, setLocalAvsDecline] = useState(avsDeclineConfig?.value);
  const [localCardAcceptance, setLocalCardAcceptance] = useState(
    nonAcceptedCardBrandsConfig?.value,
  );

  const [localBillingCredential, setLocalBillingCredential] = useState(billingCredential?.data);

  // Bank state
  const [localBankEnabled, setLocalBankEnabled] = useState<boolean>(!!bankEnabled);
  const [localBankCredential, setLocalBankCredential] = useState<BankCredentialData | undefined>(
    bankCredential?.data,
  );

  const [localCredentials, setLocalCredentials] = useState(
    paymentCredentials.reduce((acc, { systemOfOrigin, data }) => {
      const gateway = findPaymentGatewayType(systemOfOrigin);
      return gateway ? { ...acc, [gateway]: data } : acc;
    }, {}),
  );

  const [localSelectedGateway, setLocalSelectedGateway] = useState(
    findPaymentGatewayType(selectedPaymentCredential),
  );

  const [localPaymentCredentialSharing, setLocalPaymentCredentialSharing] = useState({
    enabled: !!shopperCredentialSharingConfig?.enabled,
    value: shopperCredentialSharingConfig?.value,
  });

  const [localAdditionalTransactionData, setLocalAdditionalTransactionData] =
    useState<AdditionalTransactionDataValue>(() => ({
      ...(additionalTransactionDataConfig?.value ?? defaultAdditionalTransactionDataValue),
    }));

  const [callbackErrors, setCallbackErrors] = useState<PaymentCallbackErrors>({
    url: null,
    headerKey: null,
    headerValue: null,
  });

  // Wrap initializePaymentCallback in useCallback to prevent recreation on every render
  const initializePaymentCallback = useCallback(() => {
    // Initialize with properly handled header fields
    const headerKey =
      paymentSharingCallback?.headers && Object.keys(paymentSharingCallback.headers).length > 0
        ? Object.keys(paymentSharingCallback.headers)[0]
        : '';

    const headerValue =
      paymentSharingCallback?.headers &&
      Object.keys(paymentSharingCallback.headers).length > 0 &&
      headerKey
        ? paymentSharingCallback.headers[headerKey]
        : '';

    return {
      url: paymentSharingCallback?.url || '',
      headerKey,
      headerValue,
    };
  }, [paymentSharingCallback]);

  // Create state for payment callback using the new PaymentCallback type
  const [localPaymentCallback, setLocalPaymentCallback] =
    useState<PaymentCallback>(initializePaymentCallback);

  // Reset function to revert form state
  const onCancel = useCallback(() => {
    setLocalAvsDecline(avsDeclineConfig?.value);
    setLocalSettlementType(settlementTypeConfig?.value);
    setLocalSelectedGateway(findPaymentGatewayType(selectedPaymentCredential));
    setLocalCredentials(
      paymentCredentials.reduce((acc, { systemOfOrigin, data }) => {
        const gateway = findPaymentGatewayType(systemOfOrigin);
        return gateway ? { ...acc, [gateway]: data } : acc;
      }, {}),
    );
    setLocalCardAcceptance(nonAcceptedCardBrandsConfig?.value);
    setLocalPartialAuthEnabled(isPartialAuthorizationEnabled(partialAuthConfig));
    setLocalPartialPaymentEnabled(isPartialPaymentEnabled(partialPaymentConfig));
    setLocalPaymentPlatformEnabled(!!isPaymentPlatformConfig?.value);
    setLocalSplitPaymentEnabled({
      enabled: !!splitPaymentsConfig?.enabled,
      maxPayments: splitPaymentsConfig?.value?.maxPayments,
    });
    setLocalSupportedCountries({
      enabled: !!supportedCountriesConfig?.enabled,
      value: supportedCountriesConfig?.value,
    });
    setLocalBillingCredential(billingCredential?.data);
    setLocalBankEnabled(!!bankEnabled);
    setLocalBankCredential(bankCredential?.data);
    setLocalPaymentCredentialSharing({
      enabled: !!shopperCredentialSharingConfig?.enabled,
      value: shopperCredentialSharingConfig?.value,
    });
    setLocalAdditionalTransactionData({
      ...(additionalTransactionDataConfig?.value ?? defaultAdditionalTransactionDataValue),
    });

    setLocalPaymentCallback(initializePaymentCallback());

    // Clear validation errors
    setCallbackErrors({ url: null, headerKey: null, headerValue: null });

    // Clear dirty fields
    setDirtyFields(new Set());
    resetCustomizing();
  }, [
    avsDeclineConfig?.value,
    billingCredential?.data,
    bankEnabled,
    bankCredential?.data,
    isPaymentPlatformConfig?.value,
    nonAcceptedCardBrandsConfig?.value,
    partialAuthConfig,
    partialPaymentConfig,
    paymentCredentials,
    selectedPaymentCredential,
    settlementTypeConfig?.value,
    splitPaymentsConfig?.enabled,
    splitPaymentsConfig?.value?.maxPayments,
    supportedCountriesConfig?.enabled,
    supportedCountriesConfig?.value,
    shopperCredentialSharingConfig?.enabled,
    shopperCredentialSharingConfig?.value,
    additionalTransactionDataConfig?.value,
    initializePaymentCallback,
    resetCustomizing,
  ]);

  const isSaveDisabled = useMemo(() => {
    if (readOnly) return true;

    // Only require settlement type if the merchant already has a settlement type set up
    if (settlementTypeConfig?.value && localSettlementType === undefined) return true;

    // If we have available payment gateways but none selected, block save
    if (localSelectedGateway === undefined && paymentCredentials.length > 0) return true;

    if (localSupportedCountries.enabled) {
      const { countries = [], supportAll } = localSupportedCountries.value ?? {};
      if (countries.length === 0 && !supportAll) return true;
    }

    // Bank: if enabled, require merchant id
    if (localBankEnabled) {
      if (!localBankCredential?.merchant_id || localBankCredential.merchant_id.trim() === '') {
        return true;
      }
    }

    // Check if credential sharing is enabled but callback URL is empty
    const isAnySharingEnabled =
      localPaymentCredentialSharing.value?.panSharingEnabled ||
      localPaymentCredentialSharing.value?.networkTokenSharingEnabled;
    if (
      isAnySharingEnabled &&
      (!localPaymentCallback.url || localPaymentCallback.url.trim() === '')
    ) {
      return true;
    }

    // Billing Credentials need to be both filled out or both empty
    if (localBillingCredential) {
      const { billingChainId, billingMid } = localBillingCredential;
      if (
        (!billingChainId?.length && billingMid?.length) ||
        (billingChainId?.length && !billingMid?.length)
      ) {
        return true;
      }
    }

    // If gateway selected, make sure it has credentials
    if (
      localSelectedGateway &&
      !isCredentialFilled(
        localSelectedGateway,
        localCredentials[localSelectedGateway],
        localPaymentPlatformEnabled,
      )
    ) {
      return true;
    }

    if (!hasAtLeastOneAcceptedCardNetwork(localCardAcceptance?.nonAcceptedCardBrands)) {
      return true;
    }

    return false;
  }, [
    localBillingCredential,
    localCredentials,
    localBankEnabled,
    localBankCredential?.merchant_id,
    localSelectedGateway,
    localSettlementType,
    localSupportedCountries.enabled,
    localSupportedCountries.value,
    paymentCredentials.length,
    readOnly,
    localPaymentCredentialSharing.value,
    localPaymentCallback.url,
    settlementTypeConfig?.value,
    localPaymentPlatformEnabled,
    localCardAcceptance?.nonAcceptedCardBrands,
  ]);

  const handleSave = useCallback(async () => {
    // Reset validation error state
    setCallbackErrors({ url: null, headerKey: null, headerValue: null });

    // Check if credential sharing is enabled
    const isAnySharingEnabled =
      localPaymentCredentialSharing.value?.panSharingEnabled ||
      localPaymentCredentialSharing.value?.networkTokenSharingEnabled;

    const changeCreds: OnSaveChanges['credentials'] = [];
    const changeConfigs: OnSaveChanges['configs'] = [];
    const changeCallbacks: OnSaveChanges['callbacks'] = [];
    let changeSelectedGateway: OnSaveChanges['selectedGateway'];
    let changeBankEnabled: OnSaveChanges['bankEnabled'];

    // Validate settlement type if it was modified
    if (dirtyFields.has('SETTLEMENT_TYPE')) {
      if (localSelectedGateway && localSettlementType === undefined) {
        return;
      }

      changeConfigs.push({
        ...settlementTypeConfig,
        name: 'SETTLEMENT_TYPE',
        value: localSettlementType as JSONValue,
      });
    }

    // Validate credentials if they were modified
    if (dirtyFields.has('CREDENTIAL')) {
      if (
        localSelectedGateway &&
        !isCredentialFilled(
          localSelectedGateway,
          localCredentials[localSelectedGateway],
          localPaymentPlatformEnabled,
        )
      ) {
        return;
      }

      if (localSelectedGateway) {
        const existingCredential = paymentCredentials.find(
          (c) => normalizeGateway(c.systemOfOrigin) === normalizeGateway(localSelectedGateway),
        );

        if (
          localSelectedGateway === 'Stripe' &&
          !localPaymentPlatformEnabled &&
          localCredentials?.[localSelectedGateway]?.api_key
        ) {
          localCredentials[localSelectedGateway].api_key = '';
        }

        changeCreds.push({
          ...existingCredential,
          systemOfOrigin: localSelectedGateway,
          credentialType: 'payment',
          data: localCredentials[localSelectedGateway],
        });
      }
    }

    // Validate payment sharing callback
    if (dirtyFields.has('PAYMENT_SHARING_CALLBACK')) {
      if (isAnySharingEnabled) {
        const localCallbackErrors = validateCallbackSettings(localPaymentCallback);
        setCallbackErrors(localCallbackErrors);
        if (
          localCallbackErrors.url ||
          localCallbackErrors.headerKey ||
          localCallbackErrors.headerValue
        ) {
          return;
        }
      }

      // Prepare callback for saving and add it to changes
      const [headerKey, headerValue] = [
        localPaymentCallback.headerKey?.trim(),
        localPaymentCallback.headerValue?.trim(),
      ];
      const preparedCallback: CallbackToUpsert = {
        ...paymentSharingCallback,
        url: localPaymentCallback.url,
        callbackType: 'ShopperCredentialSharing',
        headers: headerKey && headerValue ? { [headerKey]: headerValue } : undefined,
      };

      changeCallbacks.push(preparedCallback);
    }

    if (dirtyFields.has('SHOPPER_CREDENTIAL_ACCESS')) {
      // Validate the callback URL if sharing is being enabled
      if (isAnySharingEnabled) {
        const localCallbackErrors = validateCallbackSettings(localPaymentCallback);
        setCallbackErrors(localCallbackErrors);
        if (
          localCallbackErrors.url ||
          localCallbackErrors.headerKey ||
          localCallbackErrors.headerValue
        ) {
          return;
        }
      }

      // Always include the config change when the sharing settings are modified
      changeConfigs.push({
        ...shopperCredentialSharingConfig,
        name: 'SHOPPER_CREDENTIAL_ACCESS',
        enabled: localPaymentCredentialSharing.enabled,
        value: localPaymentCredentialSharing.value as JSONValue,
      });
      // Note: We don't include the callback when sharing is disabled.
      // The backend will handle deletion/preservation as needed.
    }

    if (dirtyFields.has('PAYMENT_GATEWAY')) {
      changeSelectedGateway = localSelectedGateway;
    }

    if (dirtyFields.has('BANK_ENABLED')) {
      changeBankEnabled = localBankEnabled;
    }
    if (dirtyFields.has('BANK_CREDENTIAL') && localBankEnabled) {
      if (!localBankCredential?.merchant_id?.trim()) return;
      changeCreds.push({
        ...bankCredential,
        credentialType: 'bank',
        systemOfOrigin: DEFAULT_BANK_SYSTEM_OF_ORIGIN,
        data: { merchant_id: localBankCredential.merchant_id.trim() },
      });
    }

    if (dirtyFields.has('BILLING_CREDENTIAL') && localBillingCredential) {
      changeCreds.push({
        ...billingCredential,
        credentialType: 'billing',
        systemOfOrigin: 'Worldpay_Billing',
        data: localBillingCredential,
      });
    }

    if (dirtyFields.has('NON_ACCEPTED_CARD_BRANDS')) {
      if (!hasAtLeastOneAcceptedCardNetwork(localCardAcceptance?.nonAcceptedCardBrands)) {
        return;
      }
      changeConfigs.push({
        ...nonAcceptedCardBrandsConfig,
        name: 'NON_ACCEPTED_CARD_BRANDS',
        value: localCardAcceptance as JSONValue,
      });
    }

    if (dirtyFields.has('PARTIAL_AUTHORIZATIONS')) {
      changeConfigs.push({
        ...partialAuthConfig,
        name: 'PARTIAL_AUTHORIZATIONS',
        value: localPartialAuthEnabled,
      });
    }

    if (isSkipifyAdmin && dirtyFields.has('PARTIAL_PAYMENTS')) {
      changeConfigs.push({
        ...partialPaymentConfig,
        name: 'PARTIAL_PAYMENTS',
        enabled: localPartialPaymentEnabled,
        value: { enabled: localPartialPaymentEnabled },
      });
    }

    if (
      dirtyFields.has('IS_PAYMENT_PLATFORM') &&
      localPaymentPlatformEnabled !== !!isPaymentPlatformConfig?.value
    ) {
      changeConfigs.push({
        ...isPaymentPlatformConfig,
        name: 'IS_PAYMENT_PLATFORM',
        value: localPaymentPlatformEnabled,
      });
    }

    if (dirtyFields.has('SPLIT_PAYMENTS')) {
      changeConfigs.push({
        ...splitPaymentsConfig,
        name: 'SPLIT_PAYMENTS',
        enabled: localSplitPaymentEnabled.enabled,
        value: { maxPayments: localSplitPaymentEnabled.maxPayments },
      });
    }

    if (dirtyFields.has('SUPPORTED_COUNTRIES')) {
      changeConfigs.push({
        ...supportedCountriesConfig,
        name: 'SUPPORTED_COUNTRIES',
        enabled: localSupportedCountries.enabled,
        value: localSupportedCountries.value as JSONValue,
      });
    }

    if (dirtyFields.has('AVS_DECLINE')) {
      changeConfigs.push({
        ...avsDeclineConfig,
        name: 'AVS_DECLINE',
        value: localAvsDecline as JSONValue,
      });
    }

    if (dirtyFields.has('ADDITIONAL_TRANSACTION_DATA')) {
      changeConfigs.push({
        ...additionalTransactionDataConfig,
        name: 'ADDITIONAL_TRANSACTION_DATA',
        enabled: additionalTransactionDataConfig?.enabled ?? true,
        value: localAdditionalTransactionData as JSONValue,
      });
    }

    await onSave({
      credentials: changeCreds,
      selectedGateway: changeSelectedGateway,
      configs: changeConfigs,
      callbacks: changeCallbacks,
      bankEnabled: changeBankEnabled,
    });

    setDirtyFields(new Set());
    resetCustomizing();
  }, [
    avsDeclineConfig,
    additionalTransactionDataConfig,
    localAdditionalTransactionData,
    billingCredential,
    bankCredential,
    dirtyFields,
    isPaymentPlatformConfig,
    localAvsDecline,
    localBillingCredential,
    localBankEnabled,
    localBankCredential?.merchant_id,
    localCardAcceptance,
    localCredentials,
    localPartialAuthEnabled,
    localPartialPaymentEnabled,
    localPaymentPlatformEnabled,
    localSelectedGateway,
    localSettlementType,
    localSplitPaymentEnabled.enabled,
    localSplitPaymentEnabled.maxPayments,
    localSupportedCountries.enabled,
    localSupportedCountries.value,
    nonAcceptedCardBrandsConfig,
    onSave,
    partialAuthConfig,
    partialPaymentConfig,
    paymentCredentials,
    settlementTypeConfig,
    splitPaymentsConfig,
    supportedCountriesConfig,
    localPaymentCredentialSharing.enabled,
    localPaymentCredentialSharing.value,
    shopperCredentialSharingConfig,
    localPaymentCallback,
    paymentSharingCallback,
    resetCustomizing,
  ]);

  // Handle changes to the callback component
  const handleCallbackChange = (newCallback: PaymentCallback) => {
    // Save the new callback state
    setLocalPaymentCallback(newCallback);

    handleFieldChange(
      'PAYMENT_SHARING_CALLBACK',
      isEqualObj(newCallback, initializePaymentCallback()),
    );
  };

  const inheritableSaveBarLabels = useMemo(
    () => getInheritableSaveBarLabelsForDirtyFields(dirtyFields),
    [dirtyFields],
  );
  const isFormDirty = dirtyFields.size > 0;
  const isSaveBarOpen = isChildInheritedSaveBarOpen(isFormDirty);
  const saveBarInheritance = getSaveBarInheritanceProps(isSaveBarOpen, inheritableSaveBarLabels);
  const saveBarDetailTextOverride =
    isCustomizingInherited && !isFormDirty
      ? getChildCustomizeIdleSaveBarDetailText(hasDescendants)
      : saveBarInheritance.detailTextOverride;

  return (
    <>
      {showInheritedCallouts ? (
        <InheritedConfigSettingCallouts
          isCustomizingInherited={isCustomizingInherited}
          readOnly={readOnly}
          onCustomize={onCustomize}
        />
      ) : null}
      <Stack spacing={3} sx={{ maxWidth: '448px' }}>
        <PaymentCapture
          settlementType={localSettlementType}
          avsDecline={localAvsDecline}
          onChangeSettlementType={(settlementType) => {
            setLocalSettlementType(settlementType);
            handleFieldChange('SETTLEMENT_TYPE', settlementType === settlementTypeConfig?.value);
          }}
          onChangeAvsDecline={(avsDecline) => {
            setLocalAvsDecline(avsDecline);
            handleFieldChange('AVS_DECLINE', avsDecline === avsDeclineConfig?.value);
          }}
          readOnly={readOnly || inheritableSettingsDisabled}
          merchantName={merchantName}
        />
        {!localSettlementType && localSelectedGateway && (
          <Alert severity="error">
            One Payment Capture method (Manual or Immediate) must be selected
          </Alert>
        )}
        <PaymentGateway
          credentials={localCredentials}
          onCredentialChange={(gateway, data) => {
            setLocalCredentials((prev) => ({ ...prev, [gateway]: data }));
            handleFieldChange(
              'CREDENTIAL',
              isEqualObj(
                data,
                paymentCredentials.find(
                  (c) => normalizeGateway(c.systemOfOrigin) === normalizeGateway(gateway),
                )?.data,
              ),
            );
          }}
          selectedGateway={localSelectedGateway}
          onSelectedGatewayChange={(gateway) => {
            setLocalSelectedGateway(gateway);
            handleFieldChange(
              'PAYMENT_GATEWAY',
              normalizeGateway(gateway) === normalizeGateway(selectedPaymentCredential),
            );
          }}
          isPaymentPlatformEnabled={localPaymentPlatformEnabled}
        />
        {!localSelectedGateway && paymentCredentials.length > 0 && (
          <Alert severity="error">One Payment Gateway must be selected</Alert>
        )}
        <Stack spacing={2}>
          <ConfigSwitchSection
            title="Bank Payments"
            description="Enable bank payments."
            isChecked={localBankEnabled}
            onToggle={(newValue) => {
              setLocalBankEnabled(newValue);
              handleFieldChange('BANK_ENABLED', newValue === !!bankEnabled);
            }}
            switchLabelTitle="Bank payments"
            plural
            disabled={isSaving || readOnly}
          />
          <Collapse in={localBankEnabled}>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Pay by Bank merchant ID"
                helperText="This should be the account ID of your Pay by Bank provider (e.g., Aeropay)."
                value={localBankCredential?.merchant_id ?? ''}
                onChange={(e) => {
                  const next = { merchant_id: e.target.value } satisfies BankCredentialData;
                  setLocalBankCredential(next);
                  handleFieldChange('BANK_CREDENTIAL', isEqualObj(next, bankCredential?.data));
                }}
                disabled={isSaving || readOnly}
              />
            </Box>
          </Collapse>
        </Stack>
        {usingBillingFields && (
          <BillingCredentials
            billingCredential={localBillingCredential}
            onBillingCredentialChange={(data) => {
              setLocalBillingCredential(data);
              handleFieldChange('BILLING_CREDENTIAL', isEqualObj(data, billingCredential?.data));
            }}
            disabled={isSaving || readOnly}
          />
        )}
        <Stack spacing={1}>
          <CardAcceptance
            nonAcceptedCardBrands={localCardAcceptance?.nonAcceptedCardBrands}
            onNonAcceptedCardBrandsChange={(nonAcceptedCardBrands) => {
              setLocalCardAcceptance({ nonAcceptedCardBrands });
              handleFieldChange(
                'NON_ACCEPTED_CARD_BRANDS',
                areNonAcceptedCardBrandsEqual(
                  nonAcceptedCardBrands,
                  nonAcceptedCardBrandsConfig?.value?.nonAcceptedCardBrands,
                ),
              );
            }}
            disabled={isSaving || readOnly || inheritableSettingsDisabled}
          />
          {!hasAtLeastOneAcceptedCardNetwork(localCardAcceptance?.nonAcceptedCardBrands) ? (
            <Alert severity="error" size="small">
              {CARD_ACCEPTANCE_MIN_ONE_HINT}
            </Alert>
          ) : null}
        </Stack>
        <Box>
          <Typography variant="large" fontWeight="bold" sx={{ mb: 2 }}>
            Other Payment Options
          </Typography>
          <Stack spacing={3} sx={{ '& h2': { fontSize: '14px' } }}>
            <Stack spacing={1}>
              <ConfigSwitchSection
                title="Allow Partial Payments"
                description="When enabled, shoppers can choose how much to pay on eligible paylinks (up to the paylink total). Each paylink must also opt in when it is created."
                isChecked={localPartialPaymentEnabled}
                onToggle={(newValue) => {
                  setLocalPartialPaymentEnabled(newValue);
                  handleFieldChange(
                    'PARTIAL_PAYMENTS',
                    newValue === isPartialPaymentEnabled(partialPaymentConfig),
                  );
                }}
                switchLabelTitle="Partial Payments"
                disabled={
                  isSaving ||
                  readOnly ||
                  !isSkipifyAdmin ||
                  inheritableSettingsDisabled ||
                  !!partialPaymentToggleDisabledReason
                }
              />
              {partialPaymentToggleDisabledReason && (
                <Alert severity="warning" size="small">
                  {partialPaymentToggleDisabledReason}
                </Alert>
              )}
            </Stack>
            <Stack spacing={1}>
              <ConfigSwitchSection
                title="Allow Partial Authorization"
                description="When enabled, the card issuer may approve less than the full authorization amount. This is separate from shopper-chosen partial payments."
                isChecked={localPartialAuthEnabled}
                onToggle={(newValue) => {
                  setLocalPartialAuthEnabled(newValue);
                  handleFieldChange(
                    'PARTIAL_AUTHORIZATIONS',
                    newValue === isPartialAuthorizationEnabled(partialAuthConfig),
                  );
                }}
                switchLabelTitle="Partial Authorization"
                disabled={
                  isSaving ||
                  readOnly ||
                  inheritableSettingsDisabled ||
                  !!partialAuthToggleDisabledReason
                }
              />
              {partialAuthToggleDisabledReason && (
                <Alert severity="warning" size="small">
                  {partialAuthToggleDisabledReason}
                </Alert>
              )}
            </Stack>
            <ConfigSwitchSection
              title="Allow merchant to be set as a payments platform"
              description="Allow a merchant to be set as a payments platform and use own configured api_key in payment request instead of Skipify's."
              isChecked={localPaymentPlatformEnabled}
              onToggle={(newValue) => {
                setLocalPaymentPlatformEnabled(newValue);
                handleFieldChange(
                  'IS_PAYMENT_PLATFORM',
                  newValue === !!isPaymentPlatformConfig?.value,
                );
              }}
              disabled={isSaving || readOnly || inheritableSettingsDisabled}
              switchLabelTitle="Payment Platform"
            />
            <Stack spacing={1}>
              <Box sx={{ opacity: isCustomMerchant ? 1 : 0.45 }}>
                <Stack spacing={1}>
                  <ConfigSwitchSection
                    title="Allow Split Payments"
                    description="When you turn on split payments, Shoppers are able to split the balance of their order between multiple payment methods."
                    isChecked={localSplitPaymentEnabled.enabled}
                    onToggle={(newValue) => {
                      setLocalSplitPaymentEnabled((prev) => ({
                        ...prev,
                        enabled: newValue,
                        maxPayments: prev.maxPayments ?? 2,
                      }));
                      handleFieldChange(
                        'SPLIT_PAYMENTS',
                        isSplitPaymentsStateEqualToConfig(
                          {
                            enabled: newValue,
                            maxPayments: localSplitPaymentEnabled.maxPayments,
                          },
                          splitPaymentsConfig,
                        ),
                      );
                    }}
                    switchLabelTitle="Split Payments"
                    disabled={
                      isSaving ||
                      readOnly ||
                      inheritableSettingsDisabled ||
                      !isCustomMerchant ||
                      !!splitPaymentsToggleDisabledReason
                    }
                  />
                  {splitPaymentsToggleDisabledReason && (
                    <Alert severity="warning" size="small">
                      {splitPaymentsToggleDisabledReason}
                    </Alert>
                  )}
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ '& span': { fontSize: '12px' } }}
                  >
                    <Typography component="span">
                      Allow the balance of an order to be split on
                    </Typography>
                    <Select
                      sx={{
                        mx: 1,
                        height: '32px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        lineHeight: '18px',
                      }}
                      value={localSplitPaymentEnabled.maxPayments || 2}
                      onChange={(e) => {
                        setLocalSplitPaymentEnabled((prev) => ({
                          ...prev,
                          maxPayments: e.target.value as number,
                        }));
                        handleFieldChange(
                          'SPLIT_PAYMENTS',
                          isSplitPaymentsStateEqualToConfig(
                            {
                              enabled: localSplitPaymentEnabled.enabled,
                              maxPayments: e.target.value as number,
                            },
                            splitPaymentsConfig,
                          ),
                        );
                      }}
                      disabled={
                        isSaving ||
                        readOnly ||
                        inheritableSettingsDisabled ||
                        !localSplitPaymentEnabled.enabled ||
                        !isCustomMerchant
                      }
                    >
                      {[2, 3].map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography component="span">payment methods</Typography>
                  </Stack>
                </Stack>
              </Box>
              {!isCustomMerchant && (
                <Alert severity="warning" size="small" sx={{ mb: 1 }}>
                  Your Ecommerce platform does not support Split Payments. Please reach out to
                  support@skipify.com if you need assistance.
                </Alert>
              )}
            </Stack>
            <CrossBorderPayments
              disabled={isSaving || readOnly || inheritableSettingsDisabled}
              supportedCountriesValue={localSupportedCountries.value}
              isEnabled={localSupportedCountries.enabled}
              onToggleEnabled={(enabled) => {
                setLocalSupportedCountries((prev) => ({ ...prev, enabled }));
                handleFieldChange(
                  'SUPPORTED_COUNTRIES',
                  enabled === supportedCountriesConfig?.enabled,
                );
              }}
              onChangeSupportedCountries={(value) => {
                setLocalSupportedCountries((prev) => ({ ...prev, value }));
                handleFieldChange(
                  'SUPPORTED_COUNTRIES',
                  isEqualObj(value, supportedCountriesConfig?.value),
                );
              }}
            />
            <PaymentCredentialSharing
              disabled={isSaving || !isSkipifyAdmin || inheritableSettingsDisabled}
              callbackErrors={callbackErrors}
              callback={localPaymentCallback}
              onCallbackChange={handleCallbackChange}
              shopperCredentialSharingValue={localPaymentCredentialSharing.value}
              onShopperCredentialSharingChange={(newEnabled, newValue) => {
                // Explicitly preserve the enabled and value states when toggles are changed
                const updatedValue = {
                  ...newValue,
                  panSharingEnabled: newValue.panSharingEnabled,
                  networkTokenSharingEnabled: newValue.networkTokenSharingEnabled,
                };

                setLocalPaymentCredentialSharing({
                  enabled: newEnabled,
                  value: updatedValue,
                });

                const willSharingBeEnabled =
                  updatedValue.panSharingEnabled || updatedValue.networkTokenSharingEnabled;

                if (!willSharingBeEnabled) {
                  handleCallbackChange(initializePaymentCallback());
                }

                handleFieldChange(
                  'SHOPPER_CREDENTIAL_ACCESS',
                  isEqualObj(
                    updatedValue,
                    shopperCredentialSharingConfig?.value || defaultPaymentCredentialSharingValue,
                  ),
                );
              }}
            />
            <AdditionalTransactionData
              disabled={isSaving || readOnly || !isSkipifyAdmin || inheritableSettingsDisabled}
              value={localAdditionalTransactionData}
              onChange={(next) => {
                setLocalAdditionalTransactionData(next);
                handleFieldChange(
                  'ADDITIONAL_TRANSACTION_DATA',
                  isEqualObj(
                    next,
                    additionalTransactionDataConfig?.value ?? defaultAdditionalTransactionDataValue,
                  ),
                );
              }}
            />
          </Stack>
        </Box>
      </Stack>
      <ParentAwareSaveBar
        disableSave={isSaveDisabled || !isFormDirty}
        onCancel={onCancel}
        onSave={handleSave}
        saveLoading={isSaving}
        show={isSaveBarOpen && !readOnly}
        inheritableSettingLabels={saveBarInheritance.inheritableSettingLabels}
        detailTextOverride={saveBarDetailTextOverride}
      />
    </>
  );
};
